import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useState } from 'react';
import { AlertTriangle, Bell, BellRing, CheckCheck, CheckCircle2, ClipboardCheck, Info, Loader2, X, XCircle } from 'lucide-react';
import { cn } from '@/shared/lib/cn';
import { IconButton } from '@/shared/ui/IconButton';
import { Popover, PopoverAnchor, PopoverContent, PopoverTrigger } from '@/shared/ui/Popover';
import { useBreakpoint } from '@/shared/hooks/useBreakpoint';
import { enablePush, pushSupport } from '@/shared/lib/push/pushClient';
import { usePrefsStore } from '@/entities/preferences/model/prefsStore';
import { Avatar } from '@/shared/ui/Avatar';
import { useUserPhoto } from '@/features/presence/model/presenceHooks';
import { useNotificationStore } from '../model/notificationStore';
import { useNotificationsRealtime } from '../model/useNotificationsRealtime';
import type { AppNotification } from '../model/types';

const TYPE_ICON: Record<string, { icon: typeof Info; cls: string }> = {
  info: { icon: Info, cls: 'text-sky-600 bg-sky-500/12' },
  success: { icon: CheckCircle2, cls: 'text-emerald-600 bg-emerald-500/12' },
  warning: { icon: AlertTriangle, cls: 'text-amber-600 bg-amber-500/15' },
  error: { icon: XCircle, cls: 'text-red-600 bg-red-500/12' },
  approval: { icon: ClipboardCheck, cls: 'text-brand-600 bg-brand-500/12' },
};

function ago(iso: string, locale: string) {
  const diff = (new Date(iso).getTime() - Date.now()) / 1000;
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto', style: 'short' });
  const abs = Math.abs(diff);
  if (abs < 60) return rtf.format(Math.round(diff), 'second');
  if (abs < 3600) return rtf.format(Math.round(diff / 60), 'minute');
  if (abs < 86400) return rtf.format(Math.round(diff / 3600), 'hour');
  if (abs < 7 * 86400) return rtf.format(Math.round(diff / 86400), 'day');
  return new Date(iso).toLocaleDateString(locale);
}

/** Gönderen varsa fotoğrafı (köşede tür simgesi), yoksa yalnız tür simgesi. */
function NotificationIcon({ n }: { n: AppNotification }) {
  const kind = TYPE_ICON[n.notificationType ?? 'info'] ?? TYPE_ICON.info!;
  const Icon = kind.icon;
  const photo = useUserPhoto(n.senderUserId ?? '');
  if (!n.senderUserId)
    return (
      <span className={cn('mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full', kind.cls)}>
        <Icon className="h-4 w-4" />
      </span>
    );
  return (
    <span className="relative mt-0.5 shrink-0">
      <Avatar name={n.senderName || '?'} imageBase64={photo} className="h-8 w-8" />
      <span className={cn('ring-bg-elevated bg-bg-elevated absolute -right-1 -bottom-1 flex h-4 w-4 items-center justify-center rounded-full ring-2', kind.cls)}>
        <Icon className="h-2.5 w-2.5" />
      </span>
    </span>
  );
}

/** Başlıktaki bildirim zili: okunmamış rozeti + açılır bildirim merkezi. */
export function NotificationBell() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { isMobile } = useBreakpoint();
  useNotificationsRealtime();
  const { open, items, unread, hasMore, loaded, loading, setOpen, load, markRead, markAllRead, remove } = useNotificationStore();
  const desktop = usePrefsStore((s) => s.prefs.desktopNotifications);
  const [permission, setPermission] = useState(pushSupport);

  const go = (n: AppNotification) => {
    markRead(n.notificationId);
    const url = n.url;
    if (!url || url.startsWith('/?notifications')) return;
    setOpen(false);
    if (url.startsWith('/')) navigate(url);
    else if (/^https?:\/\//i.test(url)) window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverAnchor asChild>
        <span className="relative">
          <PopoverTrigger asChild>
            <IconButton label={t('notifications.title')} aria-pressed={open}>
              {unread > 0 ? <BellRing className="h-5 w-5" /> : <Bell className="h-5 w-5" />}
            </IconButton>
          </PopoverTrigger>
          {unread > 0 ? (
            <span className="bg-danger pointer-events-none absolute -top-0.5 -right-0.5 min-w-4 rounded-full px-1 text-center text-[10px] leading-4 font-semibold text-white tabular-nums">
              {unread > 99 ? '99+' : unread}
            </span>
          ) : null}
        </span>
      </PopoverAnchor>
      <PopoverContent align="end" className={cn('flex max-h-[min(70vh,560px)] flex-col p-0', isMobile ? 'w-[calc(100vw-16px)]' : 'w-96')}>
        <div className="border-border flex items-center gap-2 border-b px-3 py-2">
          <span className="flex-1 text-sm font-semibold">{t('notifications.title')}</span>
          {unread > 0 ? (
            <button type="button" onClick={markAllRead} className="text-brand-600 hover:bg-bg-hover inline-flex items-center gap-1 rounded px-1.5 py-1 text-xs font-medium">
              <CheckCheck className="h-3.5 w-3.5" />
              {t('notifications.readAll')}
            </button>
          ) : null}
        </div>

        {desktop && permission === 'default' ? (
          <div className="bg-brand-500/8 border-border flex items-center gap-2 border-b px-3 py-2 text-xs">
            <BellRing className="text-brand-600 h-4 w-4 shrink-0" />
            <span className="flex-1">{t('notifications.enableDesktopHint')}</span>
            <button
              type="button"
              className="bg-brand-600 hover:bg-brand-700 rounded px-2 py-1 font-medium text-white"
              onClick={async () => setPermission(await enablePush(true))}
            >
              {t('notifications.enableDesktop')}
            </button>
          </div>
        ) : null}

        <div
          className="min-h-0 flex-1 overflow-y-auto overscroll-contain"
          onScroll={(e) => {
            const el = e.currentTarget;
            if (hasMore && !loading && el.scrollHeight - el.scrollTop - el.clientHeight < 60) void load(true);
          }}
        >
          {!loaded ? (
            <div className="flex justify-center py-8">
              <Loader2 className="text-fg-subtle h-5 w-5 animate-spin" />
            </div>
          ) : items.length === 0 ? (
            <div className="text-fg-subtle flex flex-col items-center gap-2 px-6 py-10 text-center text-sm">
              <Bell className="h-8 w-8 opacity-40" />
              {t('notifications.empty')}
            </div>
          ) : (
            items.map((n) => {
              return (
                <div
                  key={n.notificationId}
                  role="button"
                  tabIndex={0}
                  onClick={() => go(n)}
                  onKeyDown={(e) => e.key === 'Enter' && go(n)}
                  className={cn(
                    'group border-border hover:bg-bg-hover relative flex cursor-pointer gap-3 border-b px-3 py-2.5 last:border-b-0',
                    !n.isRead && 'bg-brand-500/5',
                  )}
                >
                  <NotificationIcon n={n} />
                  <span className="min-w-0 flex-1">
                    <span className={cn('block text-sm leading-5', !n.isRead ? 'font-semibold' : 'font-medium')}>{n.title}</span>
                    {n.body ? <span className="text-fg-muted line-clamp-3 block text-xs whitespace-pre-line">{n.body}</span> : null}
                    <span className="text-fg-subtle mt-0.5 block truncate text-[11px]">
                      {n.senderName ? <span className="text-fg-muted font-medium">{n.senderName} · </span> : null}
                      {ago(n.addDate, i18n.language)}
                    </span>
                  </span>
                  {!n.isRead ? <span className="bg-brand-500 mt-2 h-2 w-2 shrink-0 rounded-full" aria-label={t('notifications.unread')} /> : null}
                  <button
                    type="button"
                    aria-label={t('notifications.remove')}
                    title={t('notifications.remove')}
                    onClick={(e) => {
                      e.stopPropagation();
                      remove(n.notificationId);
                    }}
                    className="text-fg-subtle hover:text-fg bg-bg-elevated absolute top-1.5 right-1.5 hidden rounded p-0.5 group-hover:block focus-visible:block"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              );
            })
          )}
          {loading && loaded ? (
            <div className="flex justify-center py-2">
              <Loader2 className="text-fg-subtle h-4 w-4 animate-spin" />
            </div>
          ) : null}
        </div>
      </PopoverContent>
    </Popover>
  );
}
